import { useToast } from '~/hooks/use-toast';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CheckIcon,
  ChevronDown,
  ClipboardCheck,
  Download,
  Info,
  X,
} from 'lucide-react';
import { apiClient } from '~/lib/apiClient';
import { downloadCsvTemplate, cn } from '~/lib/utils';
import { useAuthStore } from '~/stores/useAuthStore';
import { useActiveTeam } from '~/stores/useTeamStore';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '~/components/ui/Accordion';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/components/ui/Card';
import { Separator } from '~/components/ui/Separator';
import { Label } from '~/components/ui/Label';
import { Input } from '~/components/ui/Input';
import { Button } from '~/components/ui/Button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/Table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/Dialog';
import ConnectorDocumentation from '~/components/connector/ConnectorDocumentation';
import { type SetupGuideData } from '~/components/connector/ConnectorSetupGuide';

interface PastUpload {
  id: number;
  file_name: string;
  file_type: string;
  uploaded_by: string;
  upload_date: string;
  product_line: string | null;
}

interface ProductOption {
  key: string;
  label: string;
}

interface InventoryTabProps {
  connectorType: string;
  connectorLabel: string;
  products?: readonly ProductOption[];
  uploadNote?: string;
}

const inventorySetupGuide: SetupGuideData = {
  prerequisites: [
    'Access to download CSV files from your platform dashboard',
    'Properly formatted CSV files for inventory data upload',
    'Understanding of required data format for inventory data',
  ],
  steps: [
    {
      title: 'Prepare CSV File',
      description:
        'Format your inventory data according to the required specifications',
      note: 'Ensure all required columns are present and data is properly formatted',
    },
    {
      title: 'Upload File',
      description: 'Use the file upload form to submit your CSV file',
      note: 'File will be validated and processed automatically upon upload',
    },
    {
      title: 'Monitor Upload Status',
      description:
        'Check the upload history to track the status of your file submissions',
      note: 'You can revert uploads if needed using the Past Upload History section',
    },
  ],
};

export function InventoryTab({
  connectorType,
  connectorLabel,
  products,
  uploadNote,
}: InventoryTabProps) {
  const { toast } = useToast();
  const refreshTeams = useAuthStore((state) => state.refreshTeams);
  const activeTeam = useActiveTeam();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const requiresProductSelection = !!products?.length;
  const getProductLabel = (productLine: string | null) => {
    if (!productLine) return '-';
    const match = products?.find(
      (option) => option.key.toUpperCase() === productLine.toUpperCase()
    );
    return match?.label ?? productLine;
  };
  const INVENTORY_COLUMNS_MAPPING: Record<string, string[]> = {
    instamart: [
      'CITY',
      'SKU',
      'SKUNAME',
      'COMPANY',
      'BRAND',
      'L_ONE',
      'L_TWO',
      'WHNAME',
      'MRP',
      'SHELF_LIFE',
      'CASE_SIZE',
      'LEAD_TIME',
      'WH_STOCK_QTY',
      'TOTAL_OPEN_PO_QTY',
      'WH_DOH',
      'INTRANSIT_DOH',
      'DOH_CATEGORY',
      'OLDEST_PO_DATE',
      'LAST_GRN_DATE',
      'LATEST_CLOSED_PO',
      'POQTY',
      'GRNQTY',
      'OPEN_POCODES',
      'Month',
      'Year',
    ],
    blinkit: [
      'created_at',
      'backend_facility_name',
      'backend_facility_id',
      'item_id',
      'item_name',
      'Inventory',
      'Month',
      'Year',
    ],
    blinkit_custom: ['DATE', 'PRODUCT_NAMES', 'CITY', 'INVENTORY'],
    zepto: [
      'City',
      'SKU Name',
      'SKU Code',
      'EAN',
      'SKU Category',
      'SKU Sub Category',
      'Brand Name',
      'Manufacturer Name',
      'Manufacturer ID',
      'Units',
      'Month',
      'Year',
    ],
  };

  const columns = useMemo(() => {
    return INVENTORY_COLUMNS_MAPPING[connectorType] || [];
  }, [connectorType]);

  const [files, setFiles] = useState<File[]>([]);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [history, setHistory] = useState<PastUpload[]>();
  const [revertLoading, setRevertLoading] = useState(false);

  const filteredHistory = (history ?? []).filter(
    (item) =>
      !selectedProduct ||
      item.product_line?.toUpperCase() === selectedProduct.toUpperCase(),
  );

  async function fetchHistory() {
    try {
      const data = await apiClient.get<{ upload_details?: PastUpload[] }>(
        `/connectors/file/${connectorType}/inventory`,
      );
      if (data.upload_details) {
        setHistory(data.upload_details);
      }
    } catch {
      setHistory([]);
    }
  }

  useEffect(() => {
    if (activeTeam?.id) {
      fetchHistory();
    }
  }, [activeTeam?.id]);

  const revertUpload = async (uploadId: number) => {
    try {
      setRevertLoading(true);
      const data = await apiClient.delete<{
        message?: string;
        detail?: string;
      }>(`/connectors/file/${connectorType}/imports/${uploadId}`);

      if (data.message) {
        fetchHistory();
        refreshTeams();
        toast({
          title: 'Success',
          description: data.message || 'Upload reverted back successfully',
        });
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to revert upload',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to revert upload',
        variant: 'destructive',
      });
    } finally {
      setRevertLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setMissingColumns([]);
    if (file) {
      setFiles([file]);
    }
  };

  function reset() {
    setFiles([]);
    setMissingColumns([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!files.length) return;

    setIsLoading(true);
    setMissingColumns([]);

    try {
      const formData = new FormData();
      formData.append('file', files[0]);
      if (selectedProduct) {
        formData.append('product', selectedProduct);
      }

      const response = await apiClient.postForm<{
        message?: string;
        detail?: { missing_columns?: string[] } | string;
      }>(`/connectors/file/${connectorType}/inventory-data`, formData);

      if (response.message) {
        reset();
        toast({
          title: (
            <div className="flex items-center">
              <CheckIcon className="mr-2" />
              <span className="first-letter:capitalize">Success</span>
            </div>
          ),
          duration: 3000,
          variant: 'default',
          description: response.message,
        });
        fetchHistory();
        refreshTeams();
      } else if (response.detail && typeof response.detail === 'string') {
        toast({
          title: 'Error',
          description: response.detail || 'Upload failed',
          variant: 'destructive',
        });
        reset();
      } else if (response.detail && typeof response.detail === 'object') {
        if (response.detail?.missing_columns) {
          setMissingColumns(response.detail.missing_columns);
        }
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Upload failed',
        variant: 'destructive',
      });
      reset();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full lg:flex-1 lg:min-h-0 lg:overflow-hidden mx-auto p-4">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:h-full">
        <div className="lg:col-span-7 flex flex-col gap-4 lg:h-full min-h-0">
          <Card className="flex flex-col overflow-hidden min-h-0 transition-shadow hover:shadow-lg">
            <CardHeader className="shrink-0 pb-4">
              <CardTitle className="text-lg font-semibold">
                Inventory Upload
              </CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="flex-1 overflow-auto pt-4">
              <div className="space-y-6">
                {requiresProductSelection && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                        1
                      </span>
                      <p className="text-sm font-semibold">Select Product</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {products?.map((product) => (
                        <button
                          key={product.key}
                          type="button"
                          onClick={() => {
                            setSelectedProduct(product.key);
                            reset();
                          }}
                          className={cn(
                            'flex flex-col items-start gap-1 rounded-lg border-2 p-4 text-left transition-all',
                            selectedProduct === product.key
                              ? 'border-primary bg-primary/5 shadow-sm'
                              : 'border-border hover:border-primary/40 hover:bg-muted/50',
                          )}
                        >
                          <span
                            className={cn(
                              'text-sm font-semibold',
                              selectedProduct === product.key
                                ? 'text-primary'
                                : 'text-foreground',
                            )}
                          >
                            {product.label}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              <div
                className={cn(
                  'space-y-4',
                  requiresProductSelection && 'space-y-3',
                )}
              >
                {requiresProductSelection && (
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'flex size-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                        selectedProduct
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground',
                      )}
                    >
                      2
                    </span>
                    <p
                      className={cn(
                        'text-sm font-semibold',
                        !selectedProduct && 'text-muted-foreground',
                      )}
                    >
                      Upload File
                    </p>
                  </div>
                )}

                {requiresProductSelection && !selectedProduct ? (
                  <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    Select a product above to continue.
                  </p>
                ) : (
                <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium leading-none">
                    File Type: Inventory Data
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Upload CSV for Inventory Data
                  </p>
                  {uploadNote && (
                    <p className="text-xs text-muted-foreground">{uploadNote}</p>
                  )}
                </div>

                <Separator />
                {columns.length > 0 && (
                  <Accordion type="single" collapsible className="my-4">
                    <AccordionItem value="item-1">
                      <AccordionPrimitive.Header className="flex items-center">
                        <AccordionPrimitive.Trigger className="flex flex-1 items-center justify-between py-4 text-xs font-medium underline transition-all text-left [&[data-state=open]>svg]:rotate-180">
                          <span className="flex items-center gap-2">
                            <Info
                              size={16}
                              className="shrink-0 text-blue-500"
                            />
                            Make sure your file contains the following columns:
                          </span>
                          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
                        </AccordionPrimitive.Trigger>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            downloadCsvTemplate(
                              columns,
                              `${connectorType}-inventory-template.csv`,
                            )
                          }
                          aria-label="Download Template CSV"
                          className="ml-4"
                        >
                          <Download size={16} />
                          Download Template
                        </Button>
                      </AccordionPrimitive.Header>
                      <AccordionContent>
                        <div className="border rounded-lg p-4  max-h-60 overflow-y-auto scrollbar">
                          <ul className="list-none gap-2 grid md:grid-cols-2 lg:grid-cols-3">
                            {columns.map((column, index) => (
                              <li
                                key={index}
                                className="grid grid-cols-6 p-2 rounded-lg text-xs border gap-1"
                              >
                                <span className="flex justify-center items-center">
                                  <ClipboardCheck size={14} />
                                </span>
                                <span
                                  className="col-span-5 truncate"
                                  title={column}
                                >
                                  {column}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
                <form className="my-4" onSubmit={handleSubmit}>
                  <Label htmlFor="inventory-file">Upload your CSV file:</Label>
                  <Input
                    type="file"
                    name="file"
                    id="inventory-file"
                    multiple={false}
                    accept=".csv"
                    required
                    ref={fileInputRef}
                    onChange={handleFileChange}
                  />

                  {files.length > 0 && (
                    <div className="mt-4">
                      <Button
                        onClick={reset}
                        variant="destructive"
                        size="sm"
                        type="button"
                        aria-label="Remove File"
                      >
                        Remove File
                      </Button>
                    </div>
                  )}

                  <div className="inline-flex my-2 justify-end w-full">
                    <Button
                      type="submit"
                      disabled={!files.length || isLoading}
                      aria-label="Upload"
                    >
                      {isLoading ? 'Uploading ...' : 'Upload'}
                    </Button>
                  </div>
                </form>

                {missingColumns.length > 0 && (
                  <div className="my-4">
                    <Label className="text-red-500">
                      Missing columns in your file, upload a new file:
                    </Label>
                    <ul className="list-none text-red-500 text-sm grid md:grid-cols-3">
                      {missingColumns.map((col, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-2 border m-1 border-red-400 rounded dark:border-red-700"
                        >
                          <X size="16" />
                          {col}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                </div>
                )}
              </div>
              </div>
            </CardContent>
          </Card>

          <Card className="flex flex-col overflow-hidden flex-1 min-h-0 transition-shadow hover:shadow-lg">
            <CardHeader className="shrink-0 pb-4">
              <CardTitle className="text-lg font-semibold">
                Past Upload History
              </CardTitle>
            </CardHeader>
            <Separator />
            <div className="flex-1 min-h-0 max-h-[80svh] overflow-auto scrollbar">
              <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="min-w-37.5 font-semibold">
                        Filename
                      </TableHead>
                      <TableHead className="w-35 font-semibold">
                        File Type
                      </TableHead>
                      {requiresProductSelection && (
                        <TableHead className="w-32.5 font-semibold">
                          Product
                        </TableHead>
                      )}
                      <TableHead className="w-30 font-semibold">
                        Uploaded By
                      </TableHead>
                      <TableHead className="w-30 font-semibold">
                        Upload Date
                      </TableHead>
                      <TableHead className="w-30 font-semibold text-right">
                        Action
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHistory.map((historyItem) => (
                      <TableRow
                        key={historyItem.id}
                        className="hover:bg-muted/30"
                      >
                        <TableCell
                          className="max-w-50 truncate"
                          title={String(historyItem.file_name)}
                        >
                          {historyItem.file_name}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
                            {historyItem.file_type}
                          </span>
                        </TableCell>
                        {requiresProductSelection && (
                          <TableCell className="text-muted-foreground">
                            {getProductLabel(historyItem.product_line)}
                          </TableCell>
                        )}
                        <TableCell className="text-muted-foreground">
                          {historyItem.uploaded_by}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {historyItem.upload_date}
                        </TableCell>
                        <TableCell className="text-right">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8"
                                aria-label="Revert Upload"
                              >
                                Revert
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>Revert File Upload</DialogTitle>
                                <DialogDescription>
                                  Are you sure you want to revert this upload.
                                  This action cannot be undone.
                                </DialogDescription>
                              </DialogHeader>
                              <div className="inline-flex justify-end">
                                <Button
                                  variant="destructive"
                                  onClick={() => revertUpload(historyItem.id)}
                                  disabled={revertLoading}
                                  aria-label="Confirm Revert"
                                >
                                  {revertLoading ? 'Reverting...' : 'Confirm'}
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!filteredHistory.length && (
                      <TableRow>
                        <TableCell
                          colSpan={requiresProductSelection ? 6 : 5}
                          className="text-muted-foreground text-center py-12"
                        >
                          <div className="flex flex-col items-center gap-2">
                            <span className="text-sm">
                              {history?.length
                                ? 'No uploads found for the selected product'
                                : 'No upload history found'}
                            </span>
                            <span className="text-xs">
                              Upload a file to see it here
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
              </Table>
            </div>
          </Card>
        </div>

        <div className="lg:col-span-5 flex flex-col">
          <Card className="flex-1 flex flex-col overflow-hidden transition-shadow hover:shadow-lg">
            <CardHeader className="shrink-0 pb-4">
              <CardTitle className="text-lg font-semibold">
                Documentation
              </CardTitle>
              <CardDescription className="text-sm">Setup guide</CardDescription>
            </CardHeader>
            <Separator />
            <CardContent className="flex-1 overflow-auto pt-4">
              <ConnectorDocumentation
                setupGuide={inventorySetupGuide}
                catalogueDescription={`Explore ${connectorLabel} inventory data to be synced`}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
